import { useLocalStorage } from '@vueuse/core'
import { useAnalytics } from './analytics/useAnalytics'
import { createRequestScopedComposable } from '../utils/createRequestScopedComposable'

interface Setting {
  isEnabled: boolean
  showSettingsToggle: boolean
  isUserSelected: boolean
}

export interface Settings {
  articleSummary: Setting
}

export const USER_SETTINGS_KEY = 'app:userSettings'

export const defaultSettings = (): Settings => ({
  articleSummary: {
    isEnabled: true,
    showSettingsToggle: false,
    isUserSelected: false,
  },
})

/**
 * The settings the render reads, held in Nuxt state rather than in a closure.
 *
 * It matters where this lives. The article link is decided from
 * `articleSummary.isEnabled`, and on a server rendered page nothing constructs
 * `useUserSettings` in the browser, so a value kept inside the composable could
 * never be corrected after hydration. In `useState` it is per request on the
 * server, travels in the payload, and plugins/user-settings.client.ts can reach
 * it on mount to apply the visitor's own choice.
 */
export const userSettingsState = () =>
  useState<Settings>('user-settings', defaultSettings)

export const useUserSettings = createRequestScopedComposable(
  (siteSettings?: Ref<SiteSettings>) => {
    // The visitor's own choice lives in localStorage, which a server render
    // cannot read and must not guess at. Both sides start from the flag default
    // so the first render agrees, and the client plugin applies the stored
    // choice afterwards, which is a real re-render rather than a hydration
    // mismatch Vue would decline to repair.
    const settings = userSettingsState()

    const featureFlagSettings = computed(() => siteSettings?.value.userSettings)
    watch(
      () => featureFlagSettings.value,
      (newValue) => {
        if (newValue) {
          if (!settings.value.articleSummary.isUserSelected) {
            settings.value.articleSummary.isEnabled =
              newValue.defaultSettings.articleSummary
          }
          settings.value.articleSummary.showSettingsToggle =
            newValue.enabledSettings.articleSummary
        }
      },
      { immediate: true }
    )

    const updateArticleSummary = (isEnabled: boolean) => {
      const { track } = useAnalytics()

      settings.value.articleSummary.isUserSelected = true
      settings.value.articleSummary.isEnabled = isEnabled
      if (import.meta.client) {
        useLocalStorage<Settings>(USER_SETTINGS_KEY, defaultSettings()).value =
          {
            articleSummary: { ...settings.value.articleSummary },
          }
      }

      track('NtpSetting_Changed', {
        setting_name: 'ntp_article_summary',
        setting_value: isEnabled ? 'on' : 'off',
      })
    }

    return {
      articleSummary: settings.value.articleSummary,
      updateArticleSummary,
    }
  }
)
